import React, { useState, useCallback, useMemo } from 'react';
import { Upload, Image as ImageIcon, Loader2, X, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const normalizeValue = (value, multiple) => {
  if (multiple) {
    return Array.isArray(value) ? value : [];
  }
  return typeof value === 'string' ? value : '';
};

const ImageDropzone = ({
  value,
  onChange,
  label = 'Upload Image',
  className = '',
  multiple = false,
  maxFiles = 8,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const values = useMemo(() => normalizeValue(value, multiple), [value, multiple]);

  const uploadFile = useCallback(
    async (file) => {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return null;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be under 10MB');
        return null;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post(`${apiUrl}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data.url;
    },
    []
  );

  const uploadFiles = useCallback(
    async (files) => {
      const fileList = Array.from(files || []);
      if (!fileList.length) return;

      if (multiple) {
        const availableSlots = Math.max(0, maxFiles - values.length);
        if (availableSlots <= 0) {
          toast.error(`You can upload up to ${maxFiles} screenshots`);
          return;
        }
        if (fileList.length > availableSlots) {
          toast.info(`Only the first ${availableSlots} image(s) were added`);
        }
      }

      setIsUploading(true);

      try {
        const selectedFiles = multiple
          ? fileList.slice(0, maxFiles - values.length)
          : fileList.slice(0, 1);

        const uploadedUrls = [];
        for (const file of selectedFiles) {
          const url = await uploadFile(file);
          if (url) uploadedUrls.push(url);
        }

        if (!uploadedUrls.length) return;

        if (multiple) {
          onChange([...values, ...uploadedUrls]);
          toast.success(`${uploadedUrls.length} image(s) uploaded`);
        } else {
          onChange(uploadedUrls[0]);
          toast.success('Image uploaded');
        }
      } catch (err) {
        toast.error('Upload failed');
      } finally {
        setIsUploading(false);
      }
    },
    [maxFiles, multiple, onChange, uploadFile, values]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      uploadFiles(e.dataTransfer.files);
    },
    [uploadFiles]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e) => {
      uploadFiles(e.target.files);
      e.target.value = '';
    },
    [uploadFiles]
  );

  const handleRemove = useCallback(
    (index = null) => {
      if (!multiple) {
        onChange('');
        return;
      }

      onChange(values.filter((_, itemIndex) => itemIndex !== index));
    },
    [multiple, onChange, values]
  );

  const hasValue = multiple ? values.length > 0 : Boolean(values);

  return (
    <div className={className}>
      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
        {label}
      </label>

      {hasValue && multiple ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {values.map((imageUrl, index) => (
              <div
                key={`${imageUrl}-${index}`}
                className="relative group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]"
              >
                <img src={imageUrl} alt={`Upload ${index + 1}`} className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                >
                  <X size={14} />
                </button>
              </div>
            ))}

            {values.length < maxFiles && (
              <label
                className="rounded-2xl border-2 border-dashed border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04] min-h-40 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all"
              >
                {isUploading ? (
                  <Loader2 size={28} className="text-indigo-400 animate-spin" />
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-2xl bg-white/5 text-slate-400 flex items-center justify-center">
                      <Plus size={20} />
                    </div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Add more
                    </span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                />
              </label>
            )}
          </div>
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
            {values.length}/{maxFiles} uploaded
          </p>
        </div>
      ) : hasValue ? (
        <div className="relative group w-32 h-32 aspect-square rounded-2xl overflow-hidden border border-white/15 bg-neutral-900 shadow-xl">
          <img src={values} alt="Preview" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center gap-2">
            <label className="cursor-pointer px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-white text-[10px] font-bold uppercase tracking-wider transition-all border border-white/20">
              Replace
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </label>
            <button
              type="button"
              onClick={() => handleRemove()}
              className="px-3 py-1.5 rounded-xl bg-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider hover:bg-red-500/40 transition-all border border-red-500/30"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative w-32 h-32 aspect-square rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer overflow-hidden flex flex-col items-center justify-center
            ${
              isDragging
                ? 'border-indigo-500 bg-indigo-500/10 scale-[1.02]'
                : 'border-white/15 bg-white/[0.03] hover:border-indigo-500/40 hover:bg-indigo-500/5'
            }
          `}
        >
          <label className="w-full h-full flex flex-col items-center justify-center p-3 cursor-pointer text-center">
            {isUploading ? (
              <>
                <Loader2 size={24} className="text-indigo-400 animate-spin mb-1" />
                <span className="text-[10px] font-bold text-indigo-400 font-mono">Uploading</span>
              </>
            ) : (
              <>
                <div
                  className={`
                    w-10 h-10 rounded-xl mb-2 flex items-center justify-center transition-all duration-300
                    ${isDragging ? 'bg-indigo-500/20 text-indigo-400' : 'bg-white/5 text-slate-400'}
                  `}
                >
                  {isDragging ? <ImageIcon size={20} /> : <Upload size={20} />}
                </div>
                <span className="text-[11px] font-bold text-slate-300 leading-tight">
                  {isDragging ? 'Drop Icon' : 'Upload Icon'}
                </span>
                <span className="text-[9px] text-slate-500 font-mono mt-0.5">
                  Square PNG
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              multiple={multiple}
              className="hidden"
              onChange={handleFileSelect}
              disabled={isUploading}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;
